import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from '@dnd-kit/core';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  KanbanSquare, Mail, MessageCircle, Calendar, Phone, Bot, UserPlus,
  RefreshCw, Copy, Trash2, CreditCard, Clock, Megaphone, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  new_lead: 'border-t-amber-500',
  trial_booked: 'border-t-sky-500',
  trial_done: 'border-t-violet-500',
  second_booked: 'border-t-orange-500',
  member: 'border-t-teal-500',
};

const fmtDate = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const TYPE_ICONS = {
  appointment: <Calendar className="h-4 w-4 text-sky-600" />,
  email: <Mail className="h-4 w-4 text-amber-600" />,
  whatsapp: <MessageCircle className="h-4 w-4 text-green-600" />,
  subscription: <CreditCard className="h-4 w-4 text-teal-600" />,
};

function LeadCard({ lead, onOpen }) {
  const draggable = lead.type === 'customer';
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${lead.type}:${lead.id}`,
    disabled: !draggable,
    data: { lead },
  });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => draggable && onOpen(lead)}
      data-testid={`crm-lead-card-${lead.id}`}
      className={`bg-white rounded-lg border p-3 shadow-sm text-sm space-y-1.5 ${draggable ? 'cursor-grab hover:shadow-md' : ''} ${isDragging ? 'opacity-70 shadow-lg' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium truncate">{lead.name || '–'}</span>
        {lead.type === 'chatbot' && <Bot className="h-4 w-4 text-purple-500 shrink-0" />}
        {lead.type === 'interest' && <UserPlus className="h-4 w-4 text-amber-500 shrink-0" />}
      </div>
      {lead.email && <div className="text-xs text-gray-500 truncate flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</div>}
      {lead.phone && <div className="text-xs text-gray-500 truncate flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</div>}
      {lead.note && <div className="text-xs text-gray-400 italic line-clamp-2">"{lead.note}"</div>}
      {lead.source && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{lead.source}</Badge>}
      {lead.second_invite && (
        <div
          data-testid={`crm-second-invite-${lead.id}`}
          className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${lead.second_invite.status === 'sent' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}
        >
          <Mail className="h-3 w-3" />
          {lead.second_invite.status === 'sent'
            ? `2. Einladung gesendet ${fmtDate(lead.second_invite.at)}`
            : `2. Einladung geplant ~${fmtDate(lead.second_invite.at)}`}
        </div>
      )}
      <div className="flex items-center justify-between text-[11px] text-gray-400 pt-0.5">
        <span>{fmtDate(lead.created_at)}</span>
        {lead.type === 'customer' && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />{lead.appointments_completed}/{lead.appointments_total}
            {lead.manual_stage && <Badge variant="secondary" className="text-[9px] px-1 py-0 ml-1">manuell</Badge>}
          </span>
        )}
      </div>
    </div>
  );
}

function StageColumn({ stage, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`crm-column-${stage.id}`}
      className={`flex-1 min-w-[240px] bg-gray-50 rounded-xl border-t-4 ${STAGE_COLORS[stage.id]} ${isOver ? 'ring-2 ring-teal-400 bg-teal-50/40' : ''}`}
    >
      <div className="px-3 py-2.5 flex items-center justify-between border-b">
        <span className="font-semibold text-sm">{stage.name}</span>
        <Badge variant="secondary" data-testid={`crm-count-${stage.id}`}>{stage.count}</Badge>
      </div>
      <div className="p-2 space-y-2 max-h-[65vh] overflow-y-auto">
        {stage.leads.map((lead) => (
          <LeadCard key={`${lead.type}-${lead.id}`} lead={lead} onOpen={onOpen} />
        ))}
        {stage.leads.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">Keine Einträge</p>
        )}
      </div>
    </div>
  );
}

function TimelineDialog({ lead, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lead) return;
    setLoading(true);
    axios.get(`${API}/crm/lead/${lead.id}/timeline`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Timeline konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [lead]);

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="crm-timeline-dialog">
        <DialogHeader>
          <DialogTitle>{lead?.name} — Verlauf</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400">Lade Verlauf…</div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-gray-500 space-y-0.5">
              {data?.customer?.email && <div>📧 {data.customer.email}</div>}
              {data?.customer?.phone && <div>📱 {data.customer.phone}</div>}
              <div>Kunde seit: {fmtDate(data?.customer?.created_at)}</div>
            </div>
            <div className="space-y-2">
              {(data?.events || []).map((e, i) => (
                <div key={i} className="flex gap-2 items-start border-l-2 border-gray-200 pl-3 pb-1">
                  <div className="mt-0.5">{TYPE_ICONS[e.type] || <Clock className="h-4 w-4 text-gray-400" />}</div>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{e.title}</div>
                    <div className="text-[11px] text-gray-400">{fmtDateTime(e.at)} · {e.detail}</div>
                  </div>
                </div>
              ))}
              {(data?.events || []).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">Noch keine Interaktionen</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InterestLeadsSection() {
  const [leads, setLeads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [optinCustomers, setOptinCustomers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [totalUnique, setTotalUnique] = useState(null);
  const [activeChannel, setActiveChannel] = useState('alle');
  const [lastCampaign, setLastCampaign] = useState(null);
  const [draft, setDraft] = useState(null);
  const [discountMail, setDiscountMail] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const embedUrl = `${window.location.origin}/interesse/rapperswil`;
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="520" frameborder="0" style="border:none;border-radius:12px;"></iframe>`;

  const load = useCallback(() => {
    axios.get(`${API}/crm/interest-leads`).then((res) => setLeads(res.data.leads || [])).catch(() => {});
    axios.get(`${API}/crm/newsletter-subscribers`).then((res) => setSubscribers(res.data.subscribers || [])).catch(() => {});
    axios.get(`${API}/crm/customer-newsletter-optins`).then((res) => setOptinCustomers(res.data.customers || [])).catch(() => {});
    axios.get(`${API}/crm/audience-channels`).then((res) => { setChannels(res.data.channels || []); setTotalUnique(res.data.total_unique ?? null); }).catch(() => {});
    axios.get(`${API}/crm/interest-leads/campaigns`).then((res) => setLastCampaign((res.data.campaigns || [])[0] || null)).catch(() => {});
    axios.get(`${API}/crm/newsletter-drafts/pending`).then((res) => setDraft(res.data.draft || null)).catch(() => {});
    axios.get(`${API}/crm/rapperswil-welcome-discount`).then((res) => setDiscountMail(!!res.data.enabled)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleDiscountMail = async (checked) => {
    setDiscountMail(checked);
    try {
      await axios.put(`${API}/crm/rapperswil-welcome-discount`, { enabled: checked });
      toast.success(checked
        ? '🎁 20%-Rabattcode wird jetzt in neuen Welcome-Mails erwähnt'
        : 'Rabattcode-Erwähnung deaktiviert (bis zur Eröffnung)');
    } catch {
      setDiscountMail(!checked);
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const useDraft = async () => {
    // Zentrale Sendestelle ist Marketing → Entwurf dorthin übergeben.
    try {
      localStorage.setItem('sf_marketing_prefill', JSON.stringify({ subject: draft.subject, body: draft.html_body }));
    } catch { /* noop */ }
    try { await axios.post(`${API}/crm/newsletter-drafts/${draft.id}/dismiss`, null, { params: { used: true } }); } catch { /* noop */ }
    setDraft(null);
    window.location.href = '/campaigns';
  };

  const dismissDraft = async () => {
    try {
      await axios.post(`${API}/crm/newsletter-drafts/${draft.id}/dismiss`);
      setDraft(null);
      toast.success('Entwurf verworfen');
    } catch { toast.error('Verwerfen fehlgeschlagen'); }
  };

  const generateDraftNow = async () => {
    setDraftBusy(true);
    try {
      await axios.post(`${API}/crm/newsletter-drafts/generate-now`);
      toast.success('✨ KI-Entwurf erstellt');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Entwurf-Generierung fehlgeschlagen');
    } finally {
      setDraftBusy(false);
    }
  };

  const isNewsletter = activeChannel === 'newsletter_website';
  const isOptin = activeChannel === 'kunden_newsletter';
  const visibleLeads = (isNewsletter || isOptin) ? [] : leads.filter((l) => activeChannel === 'alle' || l.source === activeChannel);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/crm/interest-leads/${id}`);
      toast.success('Interessent gelöscht');
      load();
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const removeSubscriber = async (id) => {
    try {
      await axios.delete(`${API}/crm/newsletter-subscribers/${id}`);
      toast.success('Abonnent entfernt');
      load();
    } catch { toast.error('Entfernen fehlgeschlagen'); }
  };

  const exportCsv = () => {
    const rows = isNewsletter
      ? [['E-Mail', 'Quelle', 'Datum'], ...subscribers.map((s) => [s.email, s.source || '', fmtDate(s.timestamp)])]
      : [['Name', 'E-Mail', 'Telefon', 'Quelle', 'Datum', 'Rabattcode', 'Code-Status'],
        ...visibleLeads.map((l) => [l.first_name, l.email, l.phone || '', l.source, fmtDate(l.created_at),
          l.discount_code?.code || '', l.discount_code ? (l.discount_code.redeemed ? 'eingelöst' : l.discount_code.expired ? 'abgelaufen' : 'offen') : ''])];
    const csv = rows.map((r) => r.map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = isOptin ? 'kunden-newsletter-optin.csv' : isNewsletter ? 'newsletter-abonnenten.csv' : 'interessenten.csv';
    a.click();
  };

  const totalAll = channels.reduce((s, c) => s + c.count, 0);
  const uniqueReach = totalUnique ?? totalAll;

  return (
    <div className="bg-white rounded-xl border p-4 space-y-4" data-testid="crm-interest-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold flex items-center gap-2" title="Eindeutige Personen (Kunden + Leads/Newsletter, die noch keine Kunden sind) – ohne Doppelzählungen"><UserPlus className="h-4 w-4 text-amber-500" /> Interessenten & Kanäle ({uniqueReach})</h2>
        <div className="flex gap-2">
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => { window.location.href = '/campaigns'; }} data-testid="crm-campaign-btn">
            <Megaphone className="h-3.5 w-3.5 mr-1" /> Kampagne senden
          </Button>
          <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" data-testid="crm-phone-request-btn"
            onClick={async () => {
              if (!window.confirm('Allen Leads & Newsletter-Abonnenten OHNE Handynummer eine Nachfass-Mail senden?\n\nJeder Empfänger erhält die Mail nur einmal.')) return;
              try {
                const r = await axios.post(`${API}/crm/interest-leads/phone-request`, {});
                if (r.data.started) toast.success(`📱 Nachfass-Mail wird an ${r.data.targets} Empfänger gesendet`);
                else toast.info(r.data.message || 'Keine Empfänger gefunden');
              } catch (e) { toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen'); }
            }}>
            📱 Nummern nachfassen
          </Button>
          <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="crm-reconciliation-btn"
            onClick={async () => {
              if (!window.confirm('Datenabgleich-Report (Website ↔ CRM) jetzt per E-Mail/WhatsApp an die Admin-Empfänger senden?')) return;
              try {
                const r = await axios.post(`${API}/crm/reconciliation-report/send`, {});
                toast.success(`📋 Report gesendet (E-Mail: ${r.data.email_sent ? 'ja' : 'nein'}, WhatsApp: ${r.data.whatsapp_sent || 0})`);
              } catch (e) { toast.error(e.response?.data?.detail || 'Report fehlgeschlagen'); }
            }}>
            📋 Datenabgleich
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="crm-export-csv-btn">CSV Export</Button>
          <Button size="sm" variant="outline" data-testid="crm-copy-embed-btn"
            onClick={() => { navigator.clipboard.writeText(embedCode); toast.success('Embed-Code kopiert!'); }}>
            <Copy className="h-3.5 w-3.5 mr-1" /> iFrame-Code kopieren
          </Button>
        </div>
      </div>

      {/* 07/2026: Monatlicher KI-Newsletter-Entwurf */}
      {draft ? (
        <div className="border-2 border-violet-300 bg-violet-50 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3" data-testid="crm-newsletter-draft-banner">
          <div className="min-w-0">
            <p className="font-medium text-sm text-violet-900">✨ KI-Newsletter-Entwurf bereit{draft.focus ? ` — ${draft.focus}` : ''}</p>
            <p className="text-xs text-gray-600 truncate">Betreff: {draft.subject}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={useDraft} data-testid="crm-draft-use-btn">
              Prüfen & verwenden
            </Button>
            <Button size="sm" variant="outline" onClick={dismissDraft} data-testid="crm-draft-dismiss-btn">Verwerfen</Button>
          </div>
        </div>
      ) : (
        <button className="text-xs text-violet-600 hover:underline" onClick={generateDraftNow} disabled={draftBusy} data-testid="crm-draft-generate-now-btn">
          {draftBusy ? '⏳ KI erstellt Entwurf…' : '✨ Monats-Newsletter-Entwurf jetzt von der KI erstellen lassen (läuft sonst automatisch am 1. des Monats)'}
        </button>
      )}

      {/* 07/2026: Kanal-Filter-Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="crm-channel-tabs">
        <button
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeChannel === 'alle' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          onClick={() => setActiveChannel('alle')} data-testid="crm-channel-tab-alle">
          Alle ({uniqueReach})
        </button>
        {channels.map((c) => (
          <button key={c.key}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeChannel === c.key ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveChannel(c.key)} data-testid={`crm-channel-tab-${c.key}`}>
            {c.label} ({c.count})
          </button>
        ))}
      </div>

      {lastCampaign && (
        <p className="text-xs text-gray-500" data-testid="crm-last-campaign-info">
          Letzte Kampagne: {fmtDate(lastCampaign.started_at)} — Status: {lastCampaign.status === 'done' ? '✅ abgeschlossen' : lastCampaign.status === 'running' ? '⏳ läuft' : '❌ Fehler'}
          {lastCampaign.status === 'done' && ` (${lastCampaign.email_sent ?? 0} E-Mails, ${lastCampaign.whatsapp_sent ?? 0} WhatsApp, ${lastCampaign.failed ?? 0} fehlgeschlagen)`}
        </p>
      )}
      <p className="text-xs text-gray-500">
        Formular-URL: <a href={embedUrl} target="_blank" rel="noreferrer" className="text-teal-600 underline">{embedUrl}</a>
        {' '}— Code oben kopieren und auf der Website einbetten.
      </p>

      <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-amber-50/60 border-amber-200" data-testid="crm-welcome-discount-row">
        <Switch checked={discountMail} onCheckedChange={toggleDiscountMail} data-testid="crm-welcome-discount-toggle" />
        <span className="text-xs text-gray-700">
          🎁 <strong>20%-Rabattcode in der Welcome-Mail erwähnen</strong> — erst zur Eröffnung Rapperswil-Jona aktivieren.
          {discountMail ? ' Aktiv: Neue Leads erhalten ihren persönlichen Code direkt in der Willkommensmail.' : ' Aus: Willkommensmail ohne Rabatt-Block, es wird noch kein Code erzeugt.'}
        </span>
      </div>

      {isOptin ? (
        optinCustomers.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">Name</th><th className="pr-3">E-Mail</th><th className="pr-3">Kampagne</th>
                </tr>
              </thead>
              <tbody>
                {optinCustomers.map((c) => (
                  <tr key={c.id} className="border-b last:border-0" data-testid={`crm-optin-row-${c.id}`}>
                    <td className="py-2 pr-3 font-medium">{`${c.first_name} ${c.last_name}`.trim()}</td>
                    <td className="pr-3">{c.email}</td>
                    <td className="pr-3">{c.last_campaign_at ? <Badge variant="secondary" className="text-[10px]">📣 {fmtDate(c.last_campaign_at)}</Badge> : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-gray-400 mt-2">Opt-in verwalten die Kunden selbst (Portal/App) — deshalb kein Entfernen hier.</p>
          </div>
        )
      ) : isNewsletter ? (
        subscribers.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">E-Mail</th><th className="pr-3">Quelle</th><th className="pr-3">Datum</th><th className="pr-3">Kampagne</th><th></th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((s) => (
                  <tr key={s.id} className="border-b last:border-0" data-testid={`crm-newsletter-row-${s.id}`}>
                    <td className="py-2 pr-3 font-medium">{s.email}</td>
                    <td className="pr-3 text-gray-500">{s.source || '–'}</td>
                    <td className="pr-3 text-gray-500">{fmtDate(s.timestamp)}</td>
                    <td className="pr-3">{s.last_campaign_at ? <Badge variant="secondary" className="text-[10px]">📣 {fmtDate(s.last_campaign_at)}</Badge> : ''}</td>
                    <td>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeSubscriber(s.id)} data-testid={`crm-newsletter-delete-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : visibleLeads.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-2 pr-3">Name</th><th className="pr-3">E-Mail</th><th className="pr-3">Telefon</th><th className="pr-3">Quelle</th><th className="pr-3">Datum</th><th className="pr-3">🎁 20%-Code</th><th className="pr-3">Kampagne</th><th></th>
              </tr>
            </thead>
            <tbody>
              {visibleLeads.map((l) => (
                <tr key={l.id} className="border-b last:border-0" data-testid={`crm-interest-row-${l.id}`}>
                  <td className="py-2 pr-3 font-medium">{l.first_name}</td>
                  <td className="pr-3">{l.email}</td>
                  <td className="pr-3">{l.phone || '–'}</td>
                  <td className="pr-3"><Badge variant="outline" className="text-[10px]">{l.source}</Badge></td>
                  <td className="pr-3 text-gray-500">{fmtDate(l.created_at)}</td>
                  <td className="pr-3" data-testid={`crm-lead-discount-${l.id}`}>
                    {l.discount_code ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <code className="text-[11px] font-mono bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">{l.discount_code.code}</code>
                        {l.discount_code.redeemed ? (
                          <Badge className="text-[10px] bg-green-100 text-green-700 hover:bg-green-100">✅ eingelöst</Badge>
                        ) : l.discount_code.expired ? (
                          <Badge variant="secondary" className="text-[10px] text-gray-500">abgelaufen</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-gray-500">offen</Badge>
                        )}
                      </span>
                    ) : '–'}
                  </td>
                  <td className="pr-3">{l.last_campaign_at ? <Badge variant="secondary" className="text-[10px]">📣 {fmtDate(l.last_campaign_at)}</Badge> : ''}</td>
                  <td>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(l.id)} data-testid={`crm-interest-delete-${l.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CrmPipelinePage() {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/pipeline`);
      setStages(res.data.stages || []);
    } catch {
      toast.error('Pipeline konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over) return;
    const lead = active.data.current?.lead;
    if (!lead || lead.type !== 'customer' || lead.stage === over.id) return;
    // Optimistic update
    setStages((prev) => prev.map((s) => ({
      ...s,
      leads: s.id === over.id
        ? [{ ...lead, stage: over.id, manual_stage: true }, ...s.leads]
        : s.leads.filter((l) => !(l.type === lead.type && l.id === lead.id)),
      count: s.id === over.id ? s.count + 1 : (s.id === lead.stage ? s.count - 1 : s.count),
    })));
    try {
      await axios.patch(`${API}/crm/lead/${lead.id}/stage`, { stage: over.id });
      toast.success('Stage aktualisiert');
    } catch {
      toast.error('Stage-Update fehlgeschlagen');
      load();
    }
  };

  return (
    <div className="space-y-5" data-testid="crm-pipeline-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <KanbanSquare className="h-6 w-6 text-teal-600" /> CRM / Lead-Pipeline
          </h1>
          <p className="text-sm text-gray-500 mt-1">Wo steckt jeder Lead im Flow? Karten per Drag & Drop verschieben, klicken für den Verlauf.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="crm-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Aktualisieren
        </Button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400">Lade Pipeline…</div>
      ) : (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {stages.map((stage) => (
              <StageColumn key={stage.id} stage={stage} onOpen={setSelectedLead} />
            ))}
          </div>
        </DndContext>
      )}

      <InterestLeadsSection />

      {selectedLead && <TimelineDialog lead={selectedLead} onClose={() => setSelectedLead(null)} />}
    </div>
  );
}
